// Date of Session and Coach Session Type helpers for coach notes (ADM-96).
// Session dates are calendar dates ("YYYY-MM-DD"), so they are never passed
// to `new Date(string)`: that parses as UTC midnight and shows the previous
// day for coaches west of UTC.

export type CoachSessionType = "FIRST_SESSION" | "FOLLOW_UP_SESSION";

export const SESSION_TYPE_OPTIONS: { value: CoachSessionType; label: string }[] = [
  { value: "FIRST_SESSION", label: "First session" },
  { value: "FOLLOW_UP_SESSION", label: "Follow-up session" },
];

export const EMPTY_VALUE = "-";

const pad = (n: number) => String(n).padStart(2, "0");

// Today's calendar date in the coach's local timezone.
export const todayIsoDate = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

// Accepts "YYYY-MM-DD" or a full ISO timestamp and keeps the calendar date.
export const toIsoDate = (value: string | null | undefined): string | null => {
  const match = value?.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

export const isFutureDate = (isoDate: string, today: string = todayIsoDate()): boolean =>
  isoDate > today;

const DATE_FORMAT: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };

export const formatSessionDate = (value: string | null | undefined): string => {
  const isoDate = toIsoDate(value);
  if (!isoDate) return EMPTY_VALUE;
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", DATE_FORMAT);
};

// The date a note is listed under: its Date of Session, or the submission
// date for notes logged before the field existed.
export const formatNoteDate = (note: { sessionDate?: string | null; createdAt: string }): string =>
  toIsoDate(note.sessionDate)
    ? formatSessionDate(note.sessionDate)
    : new Date(note.createdAt).toLocaleDateString("en-US", DATE_FORMAT);

export const sessionTypeLabel = (type: string | null | undefined): string =>
  SESSION_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? EMPTY_VALUE;

// First session when the member has no submitted notes from any coach,
// otherwise Follow-up session. Drafts don't count. `totalItems` covers notes
// beyond the fetched page.
export const defaultSessionType = (
  notes: { isDraft: boolean }[],
  totalItems: number = notes.length
): CoachSessionType => {
  const hasSubmittedNote = notes.some((note) => !note.isDraft) || totalItems > notes.length;
  return hasSubmittedNote ? "FOLLOW_UP_SESSION" : "FIRST_SESSION";
};
